import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { AnalyticsChannel, AnalyticsMetric } from '@shared/analytics';
import { TrendChart } from './TrendChart';
import { Settings2, Plus, Copy, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { isAnalyticsExcluded, setAnalyticsExcluded } from '@/lib/analytics';
import { analyticsRequest } from './analyticsRequest';
import { useAnalyticsData } from './useAnalyticsData';

const POSITIONS: Record<string, string> = {
  header: '顶部',
  detail: '主题详情',
  passcode: '兑换码弹窗',
};
const SPECIAL: Record<string, string> = {
  none: '无来源信息',
  invalid: '无效渠道码',
  disabled: '停用后到达',
};
const EVENTS: Record<string, string> = {
  pv: '浏览',
  detail: '详情',
  contact: '客服',
};
const selectClass =
  'min-h-11 min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-orange-600';
const panelClass =
  'min-w-0 rounded-xl border border-slate-200 bg-white p-4 sm:p-6';
const number = (value: number) => Number(value || 0).toLocaleString('zh-CN');
const visitors = (rows: AnalyticsMetric[], event: string) =>
  rows.find((row) => row.event === event)?.visitors || 0;
function Block({
  title,
  help,
  children,
}: {
  title: string;
  help?: string;
  children: ReactNode;
}) {
  return (
    <section className={panelClass}>
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {help && <p className="mt-2 text-xs leading-5 text-slate-500">{help}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}
function Empty({ text = '当前区间暂无数据' }: { text?: string }) {
  return <p className="py-6 text-center text-sm text-slate-500">{text}</p>;
}
function DataList<T>({
  rows,
  columns,
  rowKey,
}: {
  rows: T[];
  columns: Array<{ label: string; render: (row: T) => ReactNode }>;
  rowKey: (row: T) => string;
}) {
  if (!rows.length) return <Empty />;
  return (
    <>
      <div className="hidden md:block">
        <table className="w-full table-fixed text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-500">
              {columns.map((c) => (
                <th className="px-3 py-3 font-medium" key={c.label}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className="border-b border-slate-100 last:border-0"
              >
                {columns.map((c) => (
                  <td className="break-words px-3 py-4 align-top" key={c.label}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-3 md:hidden">
        {rows.map((row) => (
          <article
            key={rowKey(row)}
            className="min-w-0 rounded-lg bg-slate-50 p-4"
          >
            <div className="break-words font-medium text-slate-900">
              {columns[0].render(row)}
            </div>
            <dl className="mt-3 space-y-2">
              {columns.slice(1).map((c) => (
                <div
                  className="flex min-w-0 items-start justify-between gap-3 text-sm"
                  key={c.label}
                >
                  <dt className="shrink-0 text-slate-500">{c.label}</dt>
                  <dd className="min-w-0 break-words text-right tabular-nums">
                    {c.render(row)}
                  </dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>
    </>
  );
}
function Kpi({
  label,
  value,
  help,
}: {
  label: string;
  value: number;
  help: string;
}) {
  return (
    <div className={panelClass}>
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-slate-900">
        {number(value)}
      </p>
      <p className="mt-2 text-xs leading-5 text-slate-500">{help}</p>
    </div>
  );
}
function ChannelMetrics({ metrics }: { metrics: AnalyticsMetric[] }) {
  return (
    <dl className="mt-4 grid grid-cols-3 gap-2">
      {(
        [
          ['到达访客', 'pv'],
          ['详情访客', 'detail'],
          ['客服访客', 'contact'],
        ] as const
      ).map(([label, event]) => (
        <div key={event}>
          <dt className="text-xs text-slate-500">{label}</dt>
          <dd className="mt-1 text-lg font-semibold tabular-nums">
            {number(visitors(metrics, event))}
          </dd>
        </div>
      ))}
    </dl>
  );
}
export function AnalyticsPanel() {
  const query = useAnalyticsData();
  const { data, archives, busy, error, grain, loadedGrain } = query;
  const [tab, setTab] = useState('overview');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [excluded, setExcluded] = useState(isAnalyticsExcluded);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [created, setCreated] = useState<AnalyticsChannel | null>(null);
  const [operationError, setOperationError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [mutating, setMutating] = useState(false);
  const [pendingCollecting, setPendingCollecting] = useState<boolean | null>(
    null,
  );
  const [topicSearch, setTopicSearch] = useState('');
  const [sort, setSort] = useState('detail_hits');
  const mounted = useRef(false);
  const mutation = useRef<AbortController | null>(null);
  const mutationLock = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      mutation.current?.abort();
    };
  }, []);
  async function mutate<T>(
    path: string,
    body: unknown,
    done: (value: T) => void,
  ) {
    if (mutationLock.current) return;
    mutationLock.current = true;
    mutation.current = new AbortController();
    setMutating(true);
    setOperationError('');
    setFeedback('');
    try {
      const value = await analyticsRequest<T>(
        path,
        body,
        mutation.current.signal,
      );
      if (!mounted.current) return;
      done(value);
      query.refresh();
    } catch (e) {
      if (mounted.current)
        setOperationError(
          e instanceof Error ? e.message : '操作未完成，请重试',
        );
    } finally {
      mutationLock.current = false;
      if (mounted.current) setMutating(false);
    }
  }
  async function copy(code: string) {
    try {
      await navigator.clipboard.writeText(location.origin + '/?c=' + code);
      if (mounted.current) setFeedback('链接已复制');
    } catch {
      if (mounted.current)
        setOperationError('复制未完成，请选择完整网址手动复制');
    }
  }
  const themeRows = (data?.topics || [])
    .filter((row) =>
      row.name
        .toLocaleLowerCase()
        .includes(topicSearch.trim().toLocaleLowerCase()),
    )
    .slice()
    .sort((a, b) =>
      sort === 'name'
        ? a.name.localeCompare(b.name, 'zh-CN')
        : b[sort as 'detail_hits' | 'detail_uv' | 'successful_claims'] -
          a[sort as 'detail_hits' | 'detail_uv' | 'successful_claims'],
    );
  const channelColumns = [
    {
      label: '渠道',
      render: (row: AnalyticsChannel) => (
        <div>
          <p className="font-medium">{row.name}</p>
          {row.note && (
            <p className="mt-1 text-xs text-slate-500">{row.note}</p>
          )}
          <a
            className="mt-2 block break-all text-xs text-orange-700 underline"
            target="_blank"
            rel="noreferrer"
            href={'/?c=' + row.code}
          >
            {location.origin}/?c={row.code}
          </a>
          <Button
            variant="ghost"
            className="mt-1 min-h-11 px-1 text-xs"
            onClick={() => void copy(row.code)}
          >
            <Copy className="size-3" />
            复制链接
          </Button>
        </div>
      ),
    },
    {
      label: '到达访客',
      render: (row: AnalyticsChannel) => number(visitors(row.metrics, 'pv')),
    },
    {
      label: '详情访客',
      render: (row: AnalyticsChannel) =>
        number(visitors(row.metrics, 'detail')),
    },
    {
      label: '客服访客',
      render: (row: AnalyticsChannel) =>
        number(visitors(row.metrics, 'contact')),
    },
    {
      label: '状态',
      render: (row: AnalyticsChannel) =>
        row.enabled ? (
          <Button
            variant="outline"
            className="min-h-11"
            disabled={mutating}
            onClick={() =>
              void mutate('channels/disable', { id: row.id }, () =>
                setFeedback('渠道链接已停用'),
              )
            }
          >
            停用
          </Button>
        ) : (
          <span className="text-xs text-slate-500">已停用</span>
        ),
    },
  ];
  return (
    <section
      aria-label="运营数据"
      className="min-w-0 space-y-6 rounded-xl bg-slate-50 p-3 sm:p-6"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">
            运营数据
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            了解访问、内容与领取情况
          </p>
        </div>
        <Button
          variant="outline"
          className="min-h-11 shrink-0 border-slate-200 bg-white"
          onClick={() => setSettingsOpen(true)}
        >
          <Settings2 className="size-4" />
          设置
        </Button>
      </header>
      <div
        aria-label="统计筛选"
        className="rounded-xl border border-slate-200 bg-white p-4"
      >
        <div className="flex flex-wrap items-end gap-3">
          <label className="min-w-0 flex-1 sm:flex-none">
            <span className="mb-1.5 block text-xs text-slate-500">
              统计区间
            </span>
            <select
              aria-label="统计区间"
              className={selectClass + ' w-full'}
              value={query.period}
              onChange={(e) => query.setPeriod(e.target.value)}
            >
              <option value="1">今天</option>
              <option value="yesterday">昨天</option>
              <option value="7">近7天</option>
              <option value="30">近30天</option>
            </select>
          </label>
          <label className="min-w-0 flex-1 sm:flex-none">
            <span className="mb-1.5 block text-xs text-slate-500">
              时间粒度
            </span>
            <select
              aria-label="时间粒度"
              className={selectClass + ' w-full'}
              value={grain}
              onChange={(e) => query.setGrain(e.target.value as 'day' | 'week')}
            >
              <option value="day">按日</option>
              <option value="week">按自然周</option>
            </select>
          </label>
          <Button
            variant="outline"
            className="min-h-11 border-slate-200 bg-white"
            onClick={() => query.refresh()}
          >
            <RefreshCw className="size-4" />
            刷新
          </Button>
        </div>
        <p
          className="mt-3 flex flex-wrap gap-x-2 gap-y-1 border-t border-slate-100 pt-3 text-xs leading-5 text-slate-500"
          data-testid="analytics-range"
        >
          <span>{busy || !data ? '查询区间' : '数据区间'}：</span>
          <span className="whitespace-nowrap tabular-nums">
            {data?.from || query.from} 至 {data?.to || query.to}
          </span>
          <span className="whitespace-nowrap">· 北京时间</span>
          {(data ? loadedGrain : grain) === 'week' && (
            <span className="whitespace-nowrap">· 周一开始</span>
          )}
        </p>
      </div>
      {query.collecting === false && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <p>
            全站行为收集已关闭。新的访问行为不会计入，已有数据和领取事实仍保留。
          </p>
        </div>
      )}
      {operationError && (
        <p role="alert" className="break-words text-sm text-red-700">
          {operationError}
        </p>
      )}
      {feedback && (
        <p role="status" className="break-words text-sm text-emerald-700">
          {feedback}
        </p>
      )}
      <Tabs value={tab} onValueChange={setTab} className="min-w-0 gap-6">
        <div className="max-w-full overflow-x-auto">
          <TabsList
            aria-label="运营数据页签"
            className="h-11 min-w-full justify-start gap-1 rounded-none border-b border-slate-200 bg-transparent p-0 sm:min-w-0"
          >
            {[
              ['overview', '概览'],
              ['channels', '渠道'],
              ['content', '内容'],
              ['claims', '领取'],
            ].map(([value, label]) => (
              <TabsTrigger
                className="min-h-11 shrink-0 flex-none rounded-none border-0 border-b-2 border-transparent px-3 text-slate-500 data-[state=active]:border-orange-600 data-[state=active]:bg-transparent data-[state=active]:text-orange-700 data-[state=active]:shadow-none"
                key={value}
                value={value}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {busy && (
          <div
            role="status"
            className={panelClass + ' py-12 text-center text-sm text-slate-500'}
          >
            正在读取当前区间数据…
          </div>
        )}
        {error && (
          <div role="alert" className={panelClass + ' space-y-3'}>
            <p className="text-sm text-red-700">{error}</p>
            <Button
              variant="outline"
              className="min-h-11"
              onClick={() => query.refresh()}
            >
              重试
            </Button>
          </div>
        )}
        {data && !busy && (
          <>
            <TabsContent value="overview" className="min-w-0 space-y-6">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 [&>div:last-child]:col-span-2 sm:[&>div:last-child]:col-span-1">
                <Kpi
                  label="浏览次数 PV"
                  value={data.overview.pv}
                  help="当前区间的网站浏览次数"
                />
                <Kpi
                  label="近似访客 UV"
                  value={data.overview.uv}
                  help="浏览器编号去重，非精确真人数"
                />
                <Kpi
                  label="首次成功领取"
                  value={data.redemptions.first_success}
                  help="来自实际领取记录，不含重复领取"
                />
              </div>
              {data.redemptions.rejected.resource > 0 && (
                <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                  <AlertTriangle className="size-5 shrink-0" />
                  <p>
                    资源配置拒绝{' '}
                    <strong>
                      {number(data.redemptions.rejected.resource)}
                    </strong>{' '}
                    次，请在“领取”页签检查。
                  </p>
                </div>
              )}
              <Block
                title="访问趋势"
                help="UV 按日或自然周独立去重；部分周只统计所选区间，不能相加得到区间 UV。"
              >
                {!data.trend.length ? (
                  <Empty />
                ) : (
                  <>
                    <div className="h-44 min-w-0 sm:h-72">
                      <TrendChart
                        rows={data.trend}
                        weekly={loadedGrain === 'week'}
                      />
                    </div>
                    <p className="flex justify-center gap-5 text-xs">
                      <span className="text-orange-700">● PV</span>
                      <span className="text-slate-500">● 近似 UV</span>
                    </p>
                    <details className="mt-4">
                      <summary className="min-h-11 cursor-pointer py-3 text-sm text-slate-600">
                        查看趋势明细
                      </summary>
                      <DataList
                        rows={data.trend}
                        rowKey={(row) => row.bucket}
                        columns={[
                          {
                            label: loadedGrain === 'week' ? '周一' : '日期',
                            render: (row) => row.bucket,
                          },
                          { label: 'PV', render: (row) => number(row.pv) },
                          { label: '近似 UV', render: (row) => number(row.uv) },
                        ]}
                      />
                    </details>
                  </>
                )}
              </Block>
              <Block
                title="客服点击"
                help="只统计现有三个入口；复制微信号不单独计数。"
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {['header', 'detail', 'passcode'].map((position) => {
                    const row = data.contacts.find(
                      (item) => item.position === position,
                    );
                    return (
                      <div
                        key={position}
                        className="rounded-lg bg-slate-50 p-4"
                      >
                        <p className="text-sm text-slate-500">
                          {POSITIONS[position]}
                        </p>
                        <p className="mt-2 text-xl font-semibold tabular-nums">
                          {number(row?.hits || 0)}{' '}
                          <span className="text-xs font-normal text-slate-500">
                            次点击
                          </span>
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          近似访客 {number(row?.visitors || 0)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </Block>
            </TabsContent>
            <TabsContent value="channels" className="min-w-0 space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-slate-500">
                  同名渠道先看整体，再查看单条链接。
                </p>
                <Button
                  className="min-h-11"
                  onClick={() => {
                    setCreated(null);
                    setName('');
                    setNote('');
                    setOperationError('');
                    setFeedback('');
                    setCreateOpen(true);
                  }}
                >
                  <Plus className="size-4" />
                  新建渠道链接
                </Button>
              </div>
              <Block
                title="同名渠道汇总"
                help="含转发。同名链接的访客独立去重，不能把逐链接 UV 简单相加。"
              >
                {!data.channelGroups.length ? (
                  <Empty />
                ) : (
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {data.channelGroups.map((group) => (
                      <article
                        className="min-w-0 rounded-lg bg-slate-50 p-4"
                        key={group.name}
                      >
                        <h4 className="break-words font-medium">
                          {group.name}
                        </h4>
                        <ChannelMetrics metrics={group.metrics} />
                      </article>
                    ))}
                  </div>
                )}
              </Block>
              <Block
                title="逐链接明细 · 含转发"
                help="渠道码保留在网址中；转发仍归原链接。停用后仍能打开网站，链接不可删除。"
              >
                <DataList
                  rows={data.links}
                  columns={channelColumns}
                  rowKey={(row) => row.id}
                />
              </Block>
              <Block
                title="特殊到达"
                help="无来源、无效渠道码和停用后到达分别展示。"
              >
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                  {['none', 'invalid', 'disabled'].map((state) => (
                    <article key={state} className="rounded-lg bg-slate-50 p-4">
                      <h4 className="text-sm font-medium">{SPECIAL[state]}</h4>
                      <ChannelMetrics
                        metrics={
                          data.specialChannels.find(
                            (row) => row.state === state,
                          )?.metrics || []
                        }
                      />
                    </article>
                  ))}
                </div>
              </Block>
            </TabsContent>
            <TabsContent value="content" className="min-w-0 space-y-6">
              <Block
                title="主题详情与领取"
                help="新详情次数和近似人数来自行为采集；成功领取来自领取记录。"
              >
                <div className="mb-4 flex flex-wrap gap-3">
                  <label className="min-w-0 flex-1">
                    <span className="mb-1.5 block text-xs text-slate-500">
                      查找主题
                    </span>
                    <Input
                      aria-label="查找主题"
                      className="min-h-11 bg-white"
                      placeholder="输入主题名称"
                      value={topicSearch}
                      onChange={(e) => setTopicSearch(e.target.value)}
                    />
                  </label>
                  <label className="min-w-0">
                    <span className="mb-1.5 block text-xs text-slate-500">
                      排序
                    </span>
                    <select
                      aria-label="主题排序"
                      className={selectClass}
                      value={sort}
                      onChange={(e) => setSort(e.target.value)}
                    >
                      <option value="detail_hits">新详情次数 ↓</option>
                      <option value="detail_uv">近似人数 ↓</option>
                      <option value="successful_claims">成功领取 ↓</option>
                      <option value="name">主题名称</option>
                    </select>
                  </label>
                </div>
                {themeRows.length ? (
                  <DataList
                    rows={themeRows}
                    rowKey={(row) => row.id}
                    columns={[
                      {
                        label: '主题',
                        render: (row) => (
                          <span className="font-medium">{row.name}</span>
                        ),
                      },
                      {
                        label: '新详情次数',
                        render: (row) => number(row.detail_hits),
                      },
                      {
                        label: '近似人数',
                        render: (row) => number(row.detail_uv),
                      },
                      {
                        label: '成功领取',
                        render: (row) => number(row.successful_claims),
                      },
                    ]}
                  />
                ) : (
                  <Empty
                    text={topicSearch ? '未找到匹配主题' : '当前区间暂无数据'}
                  />
                )}
                <details className="mt-4 border-t border-slate-100 pt-2">
                  <summary className="min-h-11 cursor-pointer py-3 text-sm text-slate-500">
                    查看历史累计与匹配说明
                  </summary>
                  <p className="mb-3 text-xs leading-5 text-slate-500">
                    历史累计含旧平台导入、不去重、不随日期条件变化，不与新详情次数相加。成功领取按原记录名称唯一匹配；改名、重名等无法可靠映射的记录单列{' '}
                    {number(data.redemptions.unmatched_topic)}{' '}
                    条，不强行归入主题。
                  </p>
                  <DataList
                    rows={themeRows}
                    rowKey={(row) => row.id}
                    columns={[
                      { label: '主题', render: (row) => row.name },
                      {
                        label: '历史累计',
                        render: (row) => (
                          <span className="text-slate-500">
                            {number(row.historical_views)}
                          </span>
                        ),
                      },
                    ]}
                  />
                </details>
              </Block>
            </TabsContent>
            <TabsContent value="claims" className="min-w-0 space-y-6">
              <Kpi
                label="首次成功领取"
                value={data.redemptions.first_success}
                help="来自实际领取记录，不含重复领取；不关联浏览器编号或渠道。"
              />
              <Block
                title="每周有成功领取的会员"
                help="按领取记录中的会员独立去重。北京时间周一开始；部分周仅统计当前区间。"
              >
                <DataList
                  rows={data.redemptions.weekly_members}
                  rowKey={(row) => row.week}
                  columns={[
                    { label: '周一', render: (row) => row.week },
                    { label: '会员数', render: (row) => number(row.members) },
                  ]}
                />
              </Block>
              <Block
                title="已记录的领取被拒"
                help="不含系统错误、网络中断和重复领取；只是已记录拒绝，不是所有失败，不计算失败率。"
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(
                    [
                      ['兑换码 / 会员状态', 'passcode'],
                      ['额度', 'quota'],
                      ['资源配置', 'resource'],
                      ['无法分类的历史原因', 'unknown'],
                    ] as const
                  ).map(([label, key]) => (
                    <div
                      key={key}
                      className={
                        'rounded-lg p-4 ' +
                        (key === 'resource' &&
                        data.redemptions.rejected.resource > 0
                          ? 'bg-red-50 text-red-800'
                          : 'bg-slate-50')
                      }
                    >
                      <p className="text-sm">{label}</p>
                      <p className="mt-2 text-2xl font-semibold tabular-nums">
                        {number(data.redemptions.rejected[key])}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs leading-5 text-slate-500">
                  无法可靠映射到当前主题的成功领取：
                  {number(data.redemptions.unmatched_topic)}{' '}
                  条。旧记录不强行归入主题。
                </p>
              </Block>
            </TabsContent>
          </>
        )}
      </Tabs>
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-h-[85dvh] overflow-y-auto border-slate-200 [&>button]:size-11 [&>button]:right-2 [&>button]:top-2">
          <DialogHeader>
            <DialogTitle>运营数据设置</DialogTitle>
            <DialogDescription>
              采集开关、浏览器排除与统计口径
            </DialogDescription>
          </DialogHeader>
          <label className="flex min-h-11 items-center justify-between gap-4 text-sm">
            <span>全站收集开关</span>
            <input
              aria-label="全站收集开关"
              type="checkbox"
              className="size-5 accent-orange-600"
              disabled={mutating || busy || query.collecting === null}
              checked={(pendingCollecting ?? query.collecting) === true}
              onChange={(e) => {
                const value = e.target.checked;
                setPendingCollecting(value);
                void mutate<{ collecting: boolean }>(
                  'settings',
                  { collecting: value },
                  (result) => {
                    query.confirmCollecting(result.collecting);
                    setFeedback('全站收集设置已保存');
                  },
                ).finally(() => {
                  if (mounted.current) setPendingCollecting(null);
                });
              }}
            />
          </label>
          <label className="flex min-h-11 items-center justify-between gap-4 text-sm">
            <span>本浏览器不计入统计</span>
            <input
              aria-label="本浏览器不计入统计"
              type="checkbox"
              className="size-5 accent-orange-600"
              checked={excluded}
              onChange={(e) => {
                try {
                  setAnalyticsExcluded(e.target.checked);
                  setExcluded(e.target.checked);
                  setFeedback('本浏览器排除设置已保存');
                  setOperationError('');
                } catch {
                  setOperationError('本浏览器无法保存排除设置');
                }
              }}
            />
          </label>
          {operationError && (
            <p role="alert" className="text-sm text-red-700">
              {operationError}
            </p>
          )}
          {feedback && (
            <p role="status" className="text-sm text-emerald-700">
              {feedback}
            </p>
          )}
          <p className="text-xs leading-5 text-slate-500">
            开关仅影响前台行为采集，不改变领取事实。浏览器排除立即生效，总开关由服务端执行。近似
            UV 使用随机浏览器编号去重，不是精确真人数；不与会员编号关联。
          </p>
          <details>
            <summary className="min-h-11 cursor-pointer py-3 text-sm">
              长期日 / 自然周汇总
            </summary>
            <p className="mb-3 text-xs leading-5 text-slate-500">
              明细保留最近 90 个北京时间自然日，删除前独立计算日和周
              UV；汇总长期保留。迟到事件按服务器接收时间归日。
            </p>
            <DataList
              rows={archives}
              rowKey={(row) => row.bucket + row.event}
              columns={[
                {
                  label: loadedGrain === 'week' ? '周一' : '日期',
                  render: (row) => String(row.bucket).slice(0, 10),
                },
                {
                  label: '行为',
                  render: (row) => EVENTS[row.event] || row.event,
                },
                { label: '次数', render: (row) => number(row.hits) },
                { label: '近似人数', render: (row) => number(row.visitors) },
              ]}
            />
          </details>
        </DialogContent>
      </Dialog>
      <Dialog
        open={createOpen}
        onOpenChange={(value) => {
          if (!mutating) setCreateOpen(value);
        }}
      >
        <DialogContent className="max-h-[85dvh] overflow-y-auto border-slate-200 [&>button]:size-11 [&>button]:right-2 [&>button]:top-2">
          <DialogHeader>
            <DialogTitle>新建渠道链接</DialogTitle>
            <DialogDescription>
              同名渠道自动汇总；转发仍归原链接。
            </DialogDescription>
          </DialogHeader>
          {!created ? (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void mutate<AnalyticsChannel>(
                  'channels',
                  { name: name.trim(), note: note.trim() },
                  (value) => {
                    setCreated(value);
                    setName('');
                    setNote('');
                    setFeedback('渠道链接已创建');
                  },
                );
              }}
            >
              <label className="block text-sm">
                <span>渠道名</span>
                <Input
                  className="mt-2 min-h-11"
                  required
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="例如：微信 · 教师交流群"
                />
              </label>
              <label className="block text-sm">
                <span>备注（可选）</span>
                <Input
                  className="mt-2 min-h-11"
                  maxLength={300}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="用于区分同一渠道的不同链接"
                />
              </label>
              <Button
                className="min-h-11 w-full"
                disabled={mutating || !name.trim()}
                type="submit"
              >
                {mutating ? '创建中…' : '生成渠道链接'}
              </Button>
            </form>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-emerald-700">
                渠道链接已创建：{created.name}
              </p>
              <label className="block text-sm">
                完整网址
                <Input
                  aria-label="新建渠道完整网址"
                  className="mt-2 min-h-11"
                  readOnly
                  value={location.origin + '/?c=' + created.code}
                  onFocus={(e) => e.target.select()}
                />
              </label>
              <Button
                className="min-h-11 w-full"
                onClick={() => void copy(created.code)}
              >
                <Copy className="size-4" />
                复制完整链接
              </Button>
              <Button
                variant="outline"
                className="min-h-11 w-full"
                onClick={() => {
                  setCreated(null);
                  setFeedback('');
                }}
              >
                继续新建
              </Button>
            </div>
          )}
          {operationError && (
            <p role="alert" className="text-sm text-red-700">
              {operationError}
            </p>
          )}
          {feedback && (
            <p role="status" className="text-sm text-emerald-700">
              {feedback}
            </p>
          )}
          <p className="text-xs leading-5 text-slate-500">
            停用后仍能打开网站，链接不可删除。
          </p>
        </DialogContent>
      </Dialog>
    </section>
  );
}
